/**
 * Workshop version of WorkflowFrame.
 *
 * Renders a visual flowchart using FlowRenderer inside the standard
 * FrameHeader + FrameFooter shell. The frame auto-sizes to fit the flow
 * graph and centers it horizontally.
 */

import { Icons } from "@/components/ui/icons";
import { FrameHeader } from "@/workshop/components/shapes/shared/frame-header";
import { FrameFooter } from "@/workshop/components/shapes/shared/frame-footer";
import { cn } from "@/lib/cn";
import { FlowRenderer } from "./flow-renderer";
import type { FlowStep } from "./types";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface WorkflowFrameProps {
  title: string;
  flowSteps: FlowStep[];
  status: "idle" | "streaming" | "error" | "done";
  agentName?: string;
  onApprove?: (stepId: string) => void;
  onReject?: (stepId: string) => void;
  onResizeStart?: React.MouseEventHandler;
  onOpenPanel?: () => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function statusColor(status: string): string {
  if (status === "streaming") return "bg-status-streaming";
  if (status === "error") return "bg-status-error";
  return "bg-muted";
}

// ---------------------------------------------------------------------------
// WorkflowFrame
// ---------------------------------------------------------------------------

export function WorkflowFrame({
  title,
  flowSteps,
  status,
  agentName,
  onApprove,
  onReject,
  onResizeStart,
  onOpenPanel,
}: WorkflowFrameProps) {
  const completedCount = flowSteps.filter(
    (s) => s.status === "completed",
  ).length;
  const totalCount = flowSteps.length;

  return (
    <div
      className={cn(
        "denker-glass-frame flex flex-col rounded-frame overflow-visible",
        onOpenPanel && "cursor-pointer",
      )}
      onClick={onOpenPanel}
      data-testid="workflow-frame"
    >
      <FrameHeader
        title={title || "Workflow"}
        icon={Icons.GitBranch}
        accentColor="bg-frame-workflow"
        agentName={agentName}
        statusColor={statusColor(status)}
        data-testid="workflow-frame-header"
      />

      <div className="flex-1 overflow-visible">
        {/* Progress bar */}
        {totalCount > 0 && (
          <div className="px-3 pt-2 pb-1">
            <div className="flex items-center justify-between text-canvas-base text-muted">
              <span>
                {completedCount}/{totalCount} steps
              </span>
              <span>
                {Math.round((completedCount / totalCount) * 100)}%
              </span>
            </div>
            <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-elevated/40">
              <div
                className="h-full rounded-full bg-accent transition-all"
                style={{
                  width: `${(completedCount / totalCount) * 100}%`,
                }}
              />
            </div>
          </div>
        )}

        {/* Flow chart — centered horizontally */}
        <div className="flex justify-center">
          <FlowRenderer
            steps={flowSteps}
            onApprove={onApprove}
            onReject={onReject}
          />
        </div>
      </div>

      <FrameFooter
        onResizeStart={onResizeStart}
        data-testid="workflow-frame-footer"
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------

export type { WorkflowFrameProps, FlowStep };
